/**
 * Notification merge helpers — pure logic for combining the two notification
 * sources (shared broadcast `notifications` + the per-user personal
 * `userNotifications/{email}/items`) in the bell, the history modal and
 * mark-read writes. No React, no Firestore: unit tested directly.
 */

import type { NotificationItem, NotificationSource } from '@/types/notification.types';

/** Root collection holding broadcast notifications. */
export const BROADCAST_NOTIFICATIONS_COLLECTION = 'notifications';

/** Treat a missing `source` as broadcast (every pre-personal notification). */
export function getNotificationSource(n: Pick<NotificationItem, 'source'>): NotificationSource {
  return n.source === 'personal' ? 'personal' : 'broadcast';
}

// ---------------------------------------------------------------------------
// Bell
// ---------------------------------------------------------------------------

/**
 * Merge visible broadcast + personal notifications for the bell:
 * de-duplicated by id (broadcast wins a collision), newest first, capped.
 * With no personal items the broadcast list is returned untouched, so
 * broadcast-only behavior (and ordering) is exactly what it was.
 */
export function mergeBellNotifications(
  broadcast: NotificationItem[],
  personal: NotificationItem[],
  cap: number
): NotificationItem[] {
  if (personal.length === 0) return broadcast;
  const seen = new Set<string>();
  const merged: NotificationItem[] = [];
  for (const item of [...broadcast, ...personal]) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    merged.push(item);
  }
  // Array.prototype.sort is stable: ties keep broadcast-before-personal order
  merged.sort((a, b) => b.timestamp - a.timestamp);
  return merged.slice(0, cap);
}

// ---------------------------------------------------------------------------
// Mark read
// ---------------------------------------------------------------------------

/** Full Firestore doc path a mark-read write must target. */
export function getNotificationDocPath(
  id: string,
  source: NotificationSource,
  personalCollectionPath: string
): string {
  return source === 'personal'
    ? `${personalCollectionPath}/${id}`
    : `${BROADCAST_NOTIFICATIONS_COLLECTION}/${id}`;
}

/** Doc paths of every unread notification (for markAllAsRead), each routed to its own source. */
export function buildMarkAllReadPaths(
  notifications: NotificationItem[],
  userEmail: string,
  personalCollectionPath: string
): string[] {
  return notifications
    .filter((n) => !n.readBy.includes(userEmail))
    .map((n) => getNotificationDocPath(n.id, getNotificationSource(n), personalCollectionPath));
}

/**
 * Which source a mark-read call targets: an explicit source wins, then the
 * source of the matching known notification, else broadcast (legacy default).
 */
export function resolveMarkReadSource(
  id: string,
  known: ReadonlyMap<string, NotificationSource>,
  explicit?: NotificationSource
): NotificationSource {
  return explicit ?? known.get(id) ?? 'broadcast';
}

// ---------------------------------------------------------------------------
// History: k-way merge across two independently paginated sources
// ---------------------------------------------------------------------------

export type HistorySourceKey = NotificationSource;

/**
 * Pagination state for one source.
 * - buffer: visible items fetched but not yet emitted
 * - frontier: timestamp of the last RAW doc fetched (visible or not). Anything
 *   not yet fetched from this source is at or older than it. null = never fetched.
 * - exhausted: the source has no more docs.
 */
export interface HistorySourceState {
  buffer: NotificationItem[];
  frontier: number | null;
  exhausted: boolean;
}

export type HistoryStates = Record<HistorySourceKey, HistorySourceState>;

export interface HistoryFetchResult {
  /** Visible items from the fetched raw page */
  items: NotificationItem[];
  /** Timestamp of the last raw doc in the page (null if the page was empty) */
  frontier: number | null;
  /** True when the raw page was short (no more docs) */
  exhausted: boolean;
}

export const HISTORY_SOURCE_KEYS: readonly HistorySourceKey[] = ['broadcast', 'personal'];

export function createHistoryStates(exhausted: Partial<Record<HistorySourceKey, boolean>> = {}): HistoryStates {
  return {
    broadcast: { buffer: [], frontier: null, exhausted: exhausted.broadcast ?? false },
    personal: { buffer: [], frontier: null, exhausted: exhausted.personal ?? false },
  };
}

/** Fold one fetched raw page into a source's state. */
export function appendHistoryPage(state: HistorySourceState, page: HistoryFetchResult): HistorySourceState {
  const seen = new Set(state.buffer.map((n) => n.id));
  const buffer = [...state.buffer];
  for (const item of page.items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    buffer.push(item);
  }
  return {
    buffer,
    frontier: page.frontier ?? state.frontier,
    exhausted: page.exhausted,
  };
}

/** Whether any source still has data to emit or fetch (drives "Load older"). */
export function historyHasMore(states: HistoryStates): boolean {
  return HISTORY_SOURCE_KEYS.some((k) => states[k].buffer.length > 0 || !states[k].exhausted);
}

export interface TakeHistoryResult {
  emitted: NotificationItem[];
  states: HistoryStates;
  /** Source that must be fetched next to emit more, or null if none can help. */
  fetchNext: HistorySourceKey | null;
}

/**
 * Emit every buffered item that is provably in global order, newest first.
 *
 * An unfetched item from a non-exhausted source is at or older than that
 * source's frontier, so a buffered item is safe iff its timestamp is >= the
 * highest frontier among non-exhausted sources (a never-fetched source blocks
 * everything). The source holding that highest frontier is the one to fetch next.
 */
export function takeHistoryPage(states: HistoryStates): TakeHistoryResult {
  let bound = -Infinity;
  let fetchNext: HistorySourceKey | null = null;
  for (const key of HISTORY_SOURCE_KEYS) {
    const s = states[key];
    if (s.exhausted) continue;
    const f = s.frontier ?? Infinity;
    if (fetchNext === null || f > bound) {
      bound = f;
      fetchNext = key;
    }
  }

  const all = HISTORY_SOURCE_KEYS.flatMap((k) => states[k].buffer);
  all.sort((a, b) => b.timestamp - a.timestamp);
  const emitted = all.filter((n) => n.timestamp >= bound);
  const emittedIds = new Set(emitted.map((n) => n.id));

  const next = {} as HistoryStates;
  for (const key of HISTORY_SOURCE_KEYS) {
    next[key] = { ...states[key], buffer: states[key].buffer.filter((n) => !emittedIds.has(n.id)) };
  }
  return { emitted, states: next, fetchNext };
}

/**
 * Run one "Load older" click: emit what is safe, fetching raw pages from the
 * source that blocks progress until `target` items are collected, every source
 * is exhausted, or `maxFetches` raw fetches were spent. Pure orchestration —
 * the actual Firestore page read is injected as `fetchPage`.
 */
export async function loadHistoryPage(
  initial: HistoryStates,
  fetchPage: (source: HistorySourceKey) => Promise<HistoryFetchResult>,
  { target, maxFetches }: { target: number; maxFetches: number }
): Promise<{ emitted: NotificationItem[]; states: HistoryStates }> {
  let states = initial;
  const collected: NotificationItem[] = [];
  let fetches = 0;

  for (;;) {
    const step = takeHistoryPage(states);
    states = step.states;
    for (const item of step.emitted) {
      if (!collected.some((c) => c.id === item.id)) collected.push(item);
    }
    if (collected.length >= target || step.fetchNext === null || fetches >= maxFetches) break;
    const page = await fetchPage(step.fetchNext);
    fetches += 1;
    states = { ...states, [step.fetchNext]: appendHistoryPage(states[step.fetchNext], page) };
  }

  return { emitted: collected, states };
}
